import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid'; // Install: npm install uuid

const FrontConsent = () => {
  const [sessionToken, setSessionToken] = useState(null);
  const [consents, setConsents] = useState({
    gps: false,
    camera: false,
    nationalId: false,
    crossBorder: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [consentSaved, setConsentSaved] = useState(false);

  // Generate or retrieve anonymous session token on component mount
  useEffect(() => {
    // Check if user already has an anonymous session
    let token = sessionStorage.getItem('anonymous_consent_token');
    
    if (!token) {
      // Generate new anonymous session ID
      token = uuidv4();
      sessionStorage.setItem('anonymous_consent_token', token);
    }
    
    setSessionToken(token);
    
    // Optional: Load previously saved consents for this session
    loadExistingConsents(token);
  }, []);

  const loadExistingConsents = async (token) => {
    try {
      const response = await fetch(`/api/anonymous-consent/${token}`);
      if (response.ok) {
        const data = await response.json();
        if (data.consents_given) {
          setConsents(data.consents_given);
          setConsentSaved(true);
        }
      }
    } catch (error) {
      console.log('No existing consent found for this session');
    }
  };

  const handleCheckboxChange = (e) => {
    setConsents({
      ...consents,
      [e.target.name]: e.target.checked,
    });
    // Mark that consent has been modified but not yet saved
    setConsentSaved(false);
  };

  const saveConsent = async () => {
    if (!sessionToken) {
      alert('Session error. Please refresh the page.');
      return;
    }

    // Validate that ALL required consents are given
    const allRequired = Object.values(consents).every(v => v === true);
    if (!allRequired) {
      alert('You must grant all permissions to use the Trade Inspection System.');
      return;
    }

    setIsLoading(true);

    // Get device fingerprint (non-identifying)
    const deviceInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`, // ✅ FIXED: Added window.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // DO NOT collect device serial numbers or MAC addresses
    };

    const consentData = {
      session_token: sessionToken,
      consents_given: consents,
      consent_version: '1.0.0',
      device_info: deviceInfo,
      action: 'grant' // or 'withdraw'
    };

    try {
      const response = await fetch('/api/anonymous-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(consentData)
      });

      if (response.ok) {
        setConsentSaved(true);
        
        // Store in localStorage that consent is complete
        localStorage.setItem('consent_completed', 'true');
        localStorage.setItem('consent_timestamp', new Date().toISOString());
        
        // Now redirect to login/registration page
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        const error = await response.json();
        alert(`Failed to save consent: ${error.message}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const withdrawConsent = async () => {
    if (!sessionToken) return;
    
    if (window.confirm('WARNING: Withdrawing consent will delete your anonymous session and prevent login. Continue?')) {
      const response = await fetch(`/api/anonymous-consent/${sessionToken}/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'withdraw' })
      });

      if (response.ok) {
        sessionStorage.removeItem('anonymous_consent_token');
        localStorage.removeItem('consent_completed');
        setConsents({
          gps: false,
          camera: false,
          nationalId: false,
          crossBorder: false,
        });
        setConsentSaved(false);
        alert('Consent withdrawn. You may restart the process.');
        window.location.reload();
      }
    }
  };

  if (consentSaved) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-green-600 text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">Consent Saved Successfully</h2>
          <p className="text-gray-600">Redirecting to login page...</p>
          <p className="text-xs text-gray-500 mt-4">Your anonymous consent token: {sessionToken?.substring(0, 8)}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-xl p-6 md:p-8">
        <div className="border-b pb-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Trade Inspection System</h1>
          <p className="text-sm text-gray-600 mt-1">
            First-time setup required. Your consent is required before accessing the system.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Session ID: <span className="font-mono">{sessionToken?.substring(0, 8)}...</span>
          </p>
        </div>

        <div className="space-y-6">
          {/* Consent Checkboxes - Same as before */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <p className="text-sm text-yellow-800">
              ⚖️ <strong>Legal Notice:</strong> Under Ethiopian Proclamation No. 1321/2024, 
              you must grant all permissions to proceed. Your consent is recorded anonymously 
              before account creation.
            </p>
          </div>

          {/* GPS Consent */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg">
            <input
              type="checkbox"
              name="gps"
              checked={consents.gps}
              onChange={handleCheckboxChange}
              className="mt-1 h-5 w-5 text-blue-600"
            />
            <div>
              <label className="font-semibold">GPS Location (Sensitive Data)</label>
              <p className="text-sm text-gray-600">Collected only during active inspections.</p>
            </div>
          </div>

          {/* Camera Consent */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg">
            <input
              type="checkbox"
              name="camera"
              checked={consents.camera}
              onChange={handleCheckboxChange}
              className="mt-1 h-5 w-5 text-blue-600"
            />
            <div>
              <label className="font-semibold">Camera Access</label>
              <p className="text-sm text-gray-600">Capture inspection photos.</p>
            </div>
          </div>

          {/* National ID Consent */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg">
           
            
          </div>


          <div className="flex gap-4 pt-6">
            <button
              onClick={saveConsent}
              disabled={isLoading || !sessionToken}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Saving Consent...' : '✓ Save Consent & Continue to Login'}
            </button>
            <button
              onClick={withdrawConsent}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded-lg"
            >
              ✗ Withdraw & Exit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrontConsent